import { t, Instance } from 'mobx-state-tree';
import i18n from '@/i18n/i18n';
import { DEFAULT_GENDER, GENDER_VALUES } from '../../types';
import { duration } from '../../utils/duration';
import { localCalendarDateFromStored } from '../../utils/dateFormatting';

export const PersonModel = t
  .model('PersonModel', {
    familyMemberId: t.string,
    personId: t.string,
    userId: t.maybeNull(t.string),
    firstName: t.string,
    lastName: t.string,
    middleName: t.maybeNull(t.string),
    birthDate: t.maybeNull(t.string),
    birthPlace: t.maybeNull(t.string),
    deathDate: t.maybeNull(t.string),
    /** living | deceased | unknown — from API; used for display and access rules */
    livingStatus: t.optional(t.enumeration('livingStatus', ['living', 'deceased', 'unknown']), 'unknown'),
    gender: t.optional(t.enumeration('gender', GENDER_VALUES), DEFAULT_GENDER),
    spouseFamilyMemberIds: t.optional(t.array(t.string), []),
    lastSelectedSpouseFamilyMemberId: t.maybeNull(t.string),
    // Placeholder state - used for unknown persons in the tree
    isPlaceholder: t.optional(t.boolean, false),
    positionMetadata: t.maybeNull(t.string), // e.g., "left-parent", "right-grandparent-left"
    /** Primary profile photo URL (thumbnail for list/tree; may be relative) */
    photoUrl: t.optional(t.maybeNull(t.string), null),
    /** Person record last updated (for members list sort) */
    updatedAt: t.optional(t.string, '')
  })
  .views((self) => ({
    get fullName(): string {
      // Placeholders always display as "Unknown"
      if (self.isPlaceholder) {
        return 'Unknown';
      }
      const middle = self.middleName ? ` ${self.middleName}` : '';
      return `${self.firstName}${middle} ${self.lastName}`;
    },
    get isAlive(): boolean {
      if (self.livingStatus === 'deceased') return false;
      if (self.livingStatus === 'living') return true;
      return !self.deathDate;
    },
    get age(): string | null {
      if (!self.birthDate) return null;
      // Don't show age for deceased persons without a death date
      if (self.livingStatus === 'deceased' && !self.deathDate) return null;
      const birth = localCalendarDateFromStored(self.birthDate);
      if (!birth) return null;
      let endDate: Date;
      if (self.deathDate) {
        const death = localCalendarDateFromStored(self.deathDate);
        if (!death) return null;
        endDate = death;
      } else {
        endDate = new Date();
      }
      const label = duration(birth, endDate);
      return label ? i18n.t('common:duration.ageOld', { duration: label }) : null;
    },
    get selectedSpouseFamilyMemberId(): string | null {
      // Return lastSelectedSpouseFamilyMemberId if it's still in spouseFamilyMemberIds, otherwise return first spouseFamilyMemberId
      if (
        self.lastSelectedSpouseFamilyMemberId &&
        self.spouseFamilyMemberIds.includes(self.lastSelectedSpouseFamilyMemberId)
      ) {
        return self.lastSelectedSpouseFamilyMemberId;
      }
      return self.spouseFamilyMemberIds.length > 0 ? self.spouseFamilyMemberIds[0] : null;
    }
  }))
  .actions((self) => ({
    updatePerson(personData: {
      firstName: string;
      lastName: string;
      middleName: string | null;
      birthDate: string | null;
      birthPlace?: string | null;
      deathDate: string | null;
      livingStatus?: 'living' | 'deceased' | 'unknown';
      gender: typeof DEFAULT_GENDER;
      familyMemberId?: string;
      personId?: string;
      userId?: string | null;
      spouseFamilyMemberIds?: string[];
      lastSelectedSpouseFamilyMemberId?: string | null;
      isPlaceholder?: boolean;
      positionMetadata?: string | null;
      photoUrl?: string | null;
      updatedAt?: string;
    }) {
      self.firstName = personData.firstName;
      self.lastName = personData.lastName;
      self.middleName = personData.middleName;
      self.birthDate = personData.birthDate;
      if (personData.birthPlace !== undefined) self.birthPlace = personData.birthPlace;
      self.deathDate = personData.deathDate;
      if (personData.livingStatus !== undefined) self.livingStatus = personData.livingStatus;
      self.gender = personData.gender;
      if (personData.familyMemberId !== undefined) self.familyMemberId = personData.familyMemberId;
      if (personData.personId !== undefined) self.personId = personData.personId;
      if (personData.userId !== undefined) self.userId = personData.userId;
      if (personData.spouseFamilyMemberIds !== undefined) {
        self.spouseFamilyMemberIds.replace(personData.spouseFamilyMemberIds);
      }
      if (personData.lastSelectedSpouseFamilyMemberId !== undefined)
        self.lastSelectedSpouseFamilyMemberId = personData.lastSelectedSpouseFamilyMemberId;
      if (personData.isPlaceholder !== undefined) self.isPlaceholder = personData.isPlaceholder;
      if (personData.positionMetadata !== undefined) self.positionMetadata = personData.positionMetadata;
      if (personData.photoUrl !== undefined) self.photoUrl = personData.photoUrl;
      if (personData.updatedAt !== undefined) self.updatedAt = personData.updatedAt;
      // Note: API calls for person updates are handled in components (e.g., PersonEditForm)
      // This action is for optimistic local state updates only
    },
    addSpouseFamilyMemberId(spouseFamilyMemberId: string) {
      if (!self.spouseFamilyMemberIds.includes(spouseFamilyMemberId)) {
        self.spouseFamilyMemberIds.push(spouseFamilyMemberId);
        self.lastSelectedSpouseFamilyMemberId = spouseFamilyMemberId;
      }
      // Note: API calls for relationship updates are handled in components/services
      // This action is for optimistic local state updates only
    },
    removeSpouseFamilyMember(spouseFamilyMemberId: string) {
      const index = self.spouseFamilyMemberIds.indexOf(spouseFamilyMemberId);
      if (index >= 0) {
        self.spouseFamilyMemberIds.splice(index, 1);
        self.lastSelectedSpouseFamilyMemberId =
          self.spouseFamilyMemberIds.length > 0 ? self.spouseFamilyMemberIds[0] : null;
      }
      // Note: API calls for relationship updates are handled in components/services
      // This action is for optimistic local state updates only
    },
    selectSpouse(spouseFamilyMemberId: string) {
      self.lastSelectedSpouseFamilyMemberId = spouseFamilyMemberId;
    }
  }));

export type PersonType = Instance<typeof PersonModel>;
